import { useEffect, useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { strings, normalizeCode } from '@singparty/shared';
import { sessionApi } from '@/lib/sessionApi';
import { ApiClientError } from '@/lib/apiClient';
import { useRealtime } from '@/hooks/useRealtime';
import {
  useConnStatus,
  useGuests,
  useSession,
  useTvConnected,
} from '@/stores/sessionStore';
import { QrCode } from '@/components/QrCode';
import { ConnectionPill } from '@/components/ConnectionPill';
import { QueueList } from '@/components/QueueList';
import { NowPlayingControls } from '@/components/NowPlayingControls';
import { SettingsSheet } from '@/components/SettingsSheet';
import { Button } from '@/components/ui/Button';
import { CopyButton } from '@/components/ui/CopyButton';
import { Logo } from '@/components/ui/Logo';

export const Route = createFileRoute('/host/console/$sid')({
  component: Console,
});

function Console() {
  const { sid } = Route.useParams();
  const navigate = useNavigate();
  useRealtime(sid);
  const session = useSession();
  const guests = useGuests();
  const tvConnected = useTvConnected();
  const status = useConnStatus();
  const [showTvSetup, setShowTvSetup] = useState(false);
  const [showSettings, setShowSettings] = useState(false);

  if (status === 'ended') {
    void navigate({ to: '/host/dashboard' });
  }

  if (!session) {
    return (
      <div className="mx-auto max-w-[560px] px-4 py-8">
        <div className="h-40 animate-pulse rounded-card bg-surface" />
      </div>
    );
  }

  const joinUrl = `${location.origin}/j/${session.code}`;

  async function endParty() {
    if (!confirm(`${strings.console.endConfirmTitle} ${strings.console.endConfirmBody}`)) return;
    await sessionApi.end(sid);
    await navigate({ to: '/host/dashboard' });
  }

  async function share() {
    const data = { title: strings.brand, text: `Join my karaoke party — code ${session!.code}`, url: joinUrl };
    if (navigator.share) {
      try {
        await navigator.share(data);
      } catch {
        /* cancelled */
      }
    } else {
      await navigator.clipboard.writeText(joinUrl);
    }
  }

  return (
    <div className="mx-auto max-w-[560px] px-4 py-6 pb-24">
      <Logo className="mb-4" />
      <ConnectionPill />

      <header className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-xl font-extrabold">{session.name}</h1>
          <HostNameEditor sid={sid} current={guests.find((g) => g.isHost)?.displayName ?? 'Host'} />
        </div>
        <TvPill connected={tvConnected} onSetup={() => setShowTvSetup(true)} />
      </header>

      {/* Now Playing + transport controls (Phase 3). */}
      <NowPlayingControls sid={sid} />

      {/* Invite block */}
      <section className="mb-6 rounded-card border border-border bg-surface p-5">
        <div className="flex items-center gap-4">
          <QrCode value={joinUrl} size={120} />
          <div className="flex-1">
            <p className="text-xs uppercase tracking-wide text-text-secondary">Party code</p>
            <p className="font-mono text-3xl font-bold tracking-[0.2em] text-primary">
              {session.code}
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              <Button variant="secondary" size="sm" onClick={share}>
                {strings.console.invite}
              </Button>
              <CopyButton value={joinUrl} label="Copy link" />
            </div>
          </div>
        </div>
        {/* Copy the link when the QR is useless (e.g. localhost can't be scanned
            from a phone). Selectable so you can hand-edit the host for LAN use. */}
        <p className="mt-4 select-all break-all rounded-pill bg-bg px-3 py-2 text-center font-mono text-xs text-text-secondary">
          {joinUrl}
        </p>
      </section>

      {/* Guests (live) */}
      <section className="mb-6">
        <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-text-secondary">
          In the room · {guests.length}
        </h2>
        <div className="flex flex-wrap gap-2">
          {guests.map((g) => (
            <span
              key={g.id}
              className="rounded-pill bg-surface-2 px-3 py-1 text-sm"
            >
              {g.isHost ? '👑 ' : '🎤 '}
              {g.displayName}
            </span>
          ))}
        </div>
      </section>

      {/* Queue */}
      <section className="mb-6">
        <div className="mb-2 flex items-center justify-between">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-text-secondary">Queue</h2>
          <Button size="sm" onClick={() => navigate({ to: '/s/$sid/add', params: { sid } })}>
            + {strings.guestHome.addSong}
          </Button>
        </div>
        <QueueList sid={sid} role="host" />
      </section>

      <div className="fixed inset-x-0 bottom-0 border-t border-border bg-bg/95 px-4 py-3 pb-safe backdrop-blur">
        <div className="mx-auto flex max-w-[560px] items-center justify-between">
          <Button variant="ghost" size="sm" onClick={() => setShowTvSetup(true)}>
            {strings.console.setUpTv}
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setShowSettings(true)}>
            {strings.console.settings}
          </Button>
          <Button variant="danger" size="sm" onClick={endParty}>
            {strings.console.endParty}
          </Button>
        </div>
      </div>

      {showTvSetup && <TvSetupSheet sid={sid} onClose={() => setShowTvSetup(false)} />}
      {showSettings && <SettingsSheet sid={sid} onClose={() => setShowSettings(false)} />}
    </div>
  );
}

/** Inline editor for the host's own display name (room presence + singer label). */
function HostNameEditor({ sid, current }: { sid: string; current: string }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(current);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!editing) setVal(current);
  }, [current, editing]);

  async function save() {
    const name = val.trim();
    setEditing(false);
    if (!name || name === current) return;
    setSaving(true);
    try {
      await sessionApi.setHostName(sid, name);
    } catch {
      setVal(current); // revert on failure
    } finally {
      setSaving(false);
    }
  }

  if (editing) {
    return (
      <input
        autoFocus
        value={val}
        maxLength={24}
        disabled={saving}
        onChange={(e) => setVal(e.target.value)}
        onBlur={() => void save()}
        onKeyDown={(e) => {
          if (e.key === 'Enter') void save();
          if (e.key === 'Escape') setEditing(false);
        }}
        className="mt-0.5 h-6 w-40 rounded-pill border border-border bg-bg px-2 text-xs outline-none focus-visible:border-primary"
      />
    );
  }

  return (
    <button onClick={() => setEditing(true)} className="text-xs text-text-secondary hover:text-text" title="Edit your name">
      Hosting as <span className="font-semibold text-text">{current}</span> ✎
    </button>
  );
}

function TvPill({ connected, onSetup }: { connected: boolean; onSetup: () => void }) {
  if (connected) {
    return (
      <span className="rounded-pill bg-accent/20 px-3 py-1 text-xs font-semibold text-accent">
        ✅ {strings.console.tvConnected}
      </span>
    );
  }
  return (
    <button
      onClick={onSetup}
      className="rounded-pill bg-amber-100 px-3 py-1 text-xs font-semibold text-amber-800"
    >
      {strings.console.noTv} — {strings.console.setUpTv}
    </button>
  );
}

function TvSetupSheet({ sid, onClose }: { sid: string; onClose: () => void }) {
  const [code, setCode] = useState('');
  const [linkMsg, setLinkMsg] = useState<string | null>(null);
  const [linkErr, setLinkErr] = useState<string | null>(null);
  const [linking, setLinking] = useState(false);
  const [tvUrl, setTvUrl] = useState<string | null>(null);

  async function link(v: string) {
    setLinking(true);
    setLinkErr(null);
    setLinkMsg(null);
    try {
      await sessionApi.linkTv(sid, normalizeCode(v));
      setLinkMsg(strings.console.tvLinked);
    } catch (err) {
      setLinkErr(err instanceof ApiClientError ? err.message : strings.console.linkTvNotFound);
    } finally {
      setLinking(false);
    }
  }

  async function openTvLink() {
    const { url } = await sessionApi.screenUrl(sid);
    setTvUrl(url);
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/60 p-0 sm:items-center" onClick={onClose}>
      <div
        className="w-full max-w-md rounded-t-card border border-border bg-surface p-6 sm:rounded-card"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="mb-4 text-lg font-bold">Set up the TV</h3>

        <div className="mb-6">
          <p className="mb-2 text-sm font-semibold">{strings.console.linkByCode}</p>
          <p className="mb-3 text-xs text-text-secondary">
            On the TV, open <span className="font-mono">{location.host}/tv</span>, then type the 4-character code shown.
          </p>
          <input
            value={code}
            onChange={(e) => {
              const v = normalizeCode(e.target.value).slice(0, 4);
              setCode(v);
              if (v.length === 4) void link(v);
            }}
            placeholder="ABCD"
            maxLength={4}
            className="h-12 w-full rounded-pill border border-border bg-bg px-4 text-center font-mono text-2xl tracking-[0.3em] outline-none focus-visible:border-primary"
          />
          {linking && <p className="mt-2 text-sm text-text-secondary">Linking…</p>}
          {linkMsg && <p className="mt-2 text-sm text-accent">{linkMsg}</p>}
          {linkErr && <p className="mt-2 text-sm text-danger">{linkErr}</p>}
        </div>

        <div className="mb-4 border-t border-border pt-4">
          <p className="mb-2 text-sm font-semibold">{strings.console.openTvLink}</p>
          <p className="mb-3 text-xs text-text-secondary">{strings.console.hdmiHint}</p>
          {tvUrl ? (
            <div className="flex flex-col items-center gap-3">
              <QrCode value={tvUrl} size={160} />
              <CopyButton value={tvUrl} label="Copy TV link" />
              <p className="select-all break-all text-center font-mono text-[10px] text-text-secondary">
                {tvUrl}
              </p>
            </div>
          ) : (
            <Button variant="secondary" size="sm" onClick={openTvLink}>
              Show TV link
            </Button>
          )}
          <p className="mt-3 text-xs text-text-secondary">{strings.console.appleTvNote}</p>
        </div>

        <Button variant="ghost" className="w-full" onClick={onClose}>
          Done
        </Button>
      </div>
    </div>
  );
}
